import { parseQualified } from "./a1";
import { expandPattern, normaliseFormula } from "./formulaPatterns";
import { CellValue } from "./workbookModel";
import { ToolResult } from "../shared/toolResult";

// The Excel operations the assistant's tools are built from.
//
// Every one of these takes an A1 reference and does one thing to it. That is a much
// smaller surface than Word's equivalent, and the reason is the addressing: Word has
// to resolve a synthesised paragraph ID against a document that may have shifted
// underneath it, and most of the complexity in its tool layer is that resolution.
// "Sheet1!B7" needs none of it -- it means the same cell before and after any edit --
// so these functions are mostly one API call and an error boundary.
//
// Note what is NOT here. There is no sort, no autofill, no delete-sheet. With
// accept/reject deliberately absent in Excel (it has no revisions API) the only safety
// net is the host's own undo, and whether an Office.js edit even lands on it is
// unconfirmed -- see docs/assumptions.md A-3. Until that is known, operations that
// rearrange or destroy data wholesale are not worth the risk of being unrecoverable.
// Adding them later is easy; explaining a silently scrambled 50,000-row sheet is not.

/** Resolves a reference to a range, defaulting to the active sheet. */
function resolveRange(context: Excel.RequestContext, reference: string): Excel.Range {
  const { sheetName, address } = parseQualified(reference);
  const sheet = sheetName
    ? context.workbook.worksheets.getItem(sheetName)
    : context.workbook.worksheets.getActiveWorksheet();
  return sheet.getRange(address);
}

/**
 * Wraps an operation so a failure becomes a ToolResult the model can read and react
 * to, rather than an exception that ends the turn.
 *
 * Excel's errors are genuinely informative -- "The range address is invalid",
 * "Worksheet does not exist" -- so the message is passed through rather than
 * flattened into something generic. A model told exactly what was wrong with its
 * address will usually fix it on the next call.
 */
async function run(
  operation: (context: Excel.RequestContext) => Promise<ToolResult>
): Promise<ToolResult> {
  try {
    return await Excel.run(operation);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : typeof error === "string" ? error : String(error);
    return { success: false, error: message };
  }
}

/** Reads values and formulas back. The escape hatch for anything a prompt elided. */
export async function readRange(
  reference: string
): Promise<ToolResult & { values?: CellValue[][]; formulas?: string[][]; address?: string }> {
  return run(async (context) => {
    const range = resolveRange(context, reference);
    range.load("address,values,formulas,rowCount,columnCount");
    await context.sync();

    // A generous but real cap. The point of this tool is to fetch what a prompt left
    // out, not to defeat the budgeting by asking for the whole sheet one call at a
    // time -- and a reply too large for the context window helps nobody.
    const cells = range.rowCount * range.columnCount;
    if (cells > 20_000) {
      return {
        success: false,
        error:
          `${range.address} is ${cells.toLocaleString()} cells, which is too large to return. ` +
          "Ask for a smaller range.",
      };
    }

    return {
      success: true,
      address: range.address,
      values: range.values as CellValue[][],
      formulas: range.formulas as string[][],
    };
  });
}

/**
 * Writes literal values. The grid must match the range's shape; Excel rejects a
 * mismatch, and passing that rejection straight through is more useful than guessing
 * at what was meant.
 */
export async function setValues(reference: string, values: CellValue[][]): Promise<ToolResult> {
  return run(async (context) => {
    const range = resolveRange(context, reference);
    range.load("address,rowCount,columnCount");
    await context.sync();

    if (values.length !== range.rowCount || values.some((r) => r.length !== range.columnCount)) {
      return {
        success: false,
        error:
          `${range.address} is ${range.rowCount} rows by ${range.columnCount} columns, ` +
          `but ${values.length} rows were supplied. The shapes must match.`,
      };
    }

    range.values = values as unknown[][];
    await context.sync();
    return {
      success: true,
      segmentId: range.address,
      detail: `Wrote ${values.length} row(s) to ${range.address}`,
    };
  });
}

/**
 * Sets a formula across a range, filling it down the way dragging would.
 *
 * The subtlety that makes this more than one assignment: Excel's `formulas` setter
 * writes each string LITERALLY. Assigning "=B2*C2" to every cell of D2:D500 puts
 * "=B2*C2" in all 499 of them, so rows 3 onwards would all compute row 2's answer --
 * a column of plausible, wrong numbers. There is no fill-down in the setter, and
 * `autoFill` is ExcelApi 1.9, above our manifest floor of 1.5.
 *
 * So the formula is adjusted per row here before it is written, reusing the same
 * pattern machinery that compresses formulas on the way in: normalise against the
 * first row to get row-relative offsets, then expand for each row in turn.
 *
 * Multi-column ranges are refused rather than half-handled. Filling ACROSS would need
 * column offsets too, and writing the same column references into three columns is
 * exactly the silent wrongness this function exists to prevent. One column at a time
 * is no hardship for the caller and it cannot be got wrong.
 */
export async function setFormula(reference: string, formula: string): Promise<ToolResult> {
  return run(async (context) => {
    const range = resolveRange(context, reference);
    range.load("address,rowCount,columnCount,rowIndex");
    await context.sync();

    if (range.columnCount > 1 && range.rowCount > 1) {
      return {
        success: false,
        error:
          `${range.address} spans ${range.columnCount} columns and ${range.rowCount} rows. ` +
          "Set a formula one column at a time so it fills down correctly.",
      };
    }

    if (range.columnCount > 1) {
      return {
        success: false,
        error:
          `${range.address} spans ${range.columnCount} columns. Filling a formula across ` +
          "columns needs each column's references adjusted, so set them one cell at a time.",
      };
    }

    // A single cell needs no adjustment; anything taller is filled down.
    if (range.rowCount === 1) {
      range.formulas = [[formula]];
    } else {
      const pattern = normaliseFormula(formula, range.rowIndex);
      range.formulas = Array.from({ length: range.rowCount }, (_unused, offset) => [
        expandPattern(pattern, range.rowIndex + offset),
      ]);
    }

    await context.sync();
    return {
      success: true,
      segmentId: range.address,
      detail:
        range.rowCount === 1
          ? `Set formula on ${range.address}`
          : `Filled formula down ${range.address} (${range.rowCount} rows)`,
    };
  });
}

export async function setNumberFormat(reference: string, format: string): Promise<ToolResult> {
  return run(async (context) => {
    const range = resolveRange(context, reference);
    range.load("address,rowCount,columnCount");
    await context.sync();

    range.numberFormat = Array.from({ length: range.rowCount }, () =>
      Array.from({ length: range.columnCount }, () => format)
    );
    await context.sync();
    return {
      success: true,
      segmentId: range.address,
      detail: `Applied format "${format}" to ${range.address}`,
    };
  });
}

export interface RangeFormatting {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** Six-digit hex, with or without the leading hash. */
  fontColour?: string;
  fillColour?: string;
  horizontalAlignment?: "Left" | "Center" | "Right";
}

const hex = (colour: string) => (colour.startsWith("#") ? colour : `#${colour}`);

export async function formatRange(
  reference: string,
  formatting: RangeFormatting
): Promise<ToolResult> {
  return run(async (context) => {
    const range = resolveRange(context, reference);
    range.load("address");
    await context.sync();

    const { format } = range;
    if (formatting.bold !== undefined) format.font.bold = formatting.bold;
    if (formatting.italic !== undefined) format.font.italic = formatting.italic;
    if (formatting.underline !== undefined) {
      format.font.underline = formatting.underline
        ? Excel.RangeUnderlineStyle.single
        : Excel.RangeUnderlineStyle.none;
    }
    if (formatting.fontColour) format.font.color = hex(formatting.fontColour);
    if (formatting.fillColour) format.fill.color = hex(formatting.fillColour);
    if (formatting.horizontalAlignment) {
      format.horizontalAlignment = formatting.horizontalAlignment;
    }

    await context.sync();
    return { success: true, segmentId: range.address, detail: `Formatted ${range.address}` };
  });
}

export type Axis = "rows" | "columns";

/**
 * Inserts blank rows or columns, shifting the rest down or right.
 *
 * `reference` names the rows or columns to insert BEFORE, as a range: "5:7" inserts
 * three rows above row 5. Expressing it as a range rather than an index-plus-count
 * keeps it in the same vocabulary as every other tool here, and means Excel does the
 * shifting arithmetic rather than us.
 */
export async function insertCells(reference: string, axis: Axis): Promise<ToolResult> {
  return run(async (context) => {
    const range = resolveRange(context, reference);
    range.load("address");
    await context.sync();

    range.insert(
      axis === "rows" ? Excel.InsertShiftDirection.down : Excel.InsertShiftDirection.right
    );
    await context.sync();
    return {
      success: true,
      segmentId: range.address,
      detail: `Inserted ${axis} at ${range.address}`,
    };
  });
}

export async function deleteCells(reference: string, axis: Axis): Promise<ToolResult> {
  return run(async (context) => {
    const range = resolveRange(context, reference);
    range.load("address,rowCount,columnCount");
    await context.sync();

    range.delete(axis === "rows" ? Excel.DeleteShiftDirection.up : Excel.DeleteShiftDirection.left);
    await context.sync();
    return {
      success: true,
      segmentId: range.address,
      detail: `Deleted ${axis === "rows" ? range.rowCount : range.columnCount} ${axis} at ${range.address}`,
    };
  });
}

export async function addSheet(name: string): Promise<ToolResult> {
  return run(async (context) => {
    const sheet = context.workbook.worksheets.add(name);
    sheet.load("name");
    await context.sync();
    return { success: true, detail: `Added sheet "${sheet.name}"` };
  });
}

/**
 * Selects a range, which also scrolls it into view.
 *
 * This is the whole of Excel's answer to what `scrollToParagraph` plus a bookmark does
 * in Word. Word needs a bookmark because a paragraph ID goes stale; an address does
 * not, so there is nothing to lay down beforehand and nothing to clean up after.
 */
export async function selectRange(reference: string): Promise<ToolResult> {
  return run(async (context) => {
    const { sheetName, address } = parseQualified(reference);
    const sheet = sheetName
      ? context.workbook.worksheets.getItem(sheetName)
      : context.workbook.worksheets.getActiveWorksheet();

    // Activate first, unconditionally. Selecting a range on a sheet the user is not
    // looking at silently does nothing useful, and "no sheet name" does NOT mean "the
    // sheet is already right" -- it means the caller left it to the active sheet,
    // which by click time may be anywhere. Activating the active sheet costs nothing.
    sheet.activate();
    const range = sheet.getRange(address);
    range.select();
    range.load("address");
    await context.sync();
    return { success: true, segmentId: range.address };
  });
}
